/**
 * Site validation utilities for equipment site changes
 * Validates site format and sanitizes user input
 */

export interface SiteValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Site format validation pattern
 * Must be 3-4 uppercase letters followed by one or more digits
 * Examples: BCN1, LEJ4, ABCD123
 */
const SITE_FORMAT_PATTERN = /^[A-Z]{3,4}\d+$/;

/**
 * Validates if a site string matches the required format
 * @param site - The site string to validate
 * @returns SiteValidationResult with validation status and error message if invalid
 */
export const validateSiteFormat = (site: string): SiteValidationResult => {
  // Check for empty or null/undefined input
  if (!site || site.trim().length === 0) {
    return {
      isValid: false,
      error: 'Site value cannot be empty'
    };
  }

  const trimmedSite = site.trim();

  // Check if site matches the required pattern
  if (!SITE_FORMAT_PATTERN.test(trimmedSite)) {
    return {
      isValid: false,
      error: 'Site must be 3-4 uppercase letters followed by numbers (e.g., BCN1, LEJ4, ABCD123)'
    };
  }

  return {
    isValid: true
  };
};

/**
 * Sanitizes site input by trimming whitespace and converting to uppercase
 * @param site - The raw site input string
 * @returns Cleaned site string
 */
export const sanitizeSiteInput = (site: string): string => {
  if (!site) {
    return '';
  }

  return site.trim().toUpperCase();
};

/**
 * Combined validation and sanitization function
 * @param site - The raw site input string
 * @returns Object with sanitized site and validation result
 */
export const validateAndSanitizeSite = (site: string): {
  sanitizedSite: string;
  validation: SiteValidationResult;
} => {
  const sanitizedSite = sanitizeSiteInput(site);
  const validation = validateSiteFormat(sanitizedSite);

  return {
    sanitizedSite,
    validation
  };
};

/**
 * Checks if the new site is different from the current site
 * @param currentSite - The currently active site
 * @param newSite - The new site to validate
 * @returns true if sites are different, false if they're the same
 */
export const isSiteChanged = (currentSite: string, newSite: string): boolean => {
  const sanitizedCurrent = sanitizeSiteInput(currentSite);
  const sanitizedNew = sanitizeSiteInput(newSite);
  
  return sanitizedCurrent !== sanitizedNew;
};